import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

interface OperatorGuide {
  symbol: string;
  nameKey: string;
  descriptionKey: string;
  exampleKey: string;
}

interface FilterTabGuide {
  id: string;
  icon: string;
  titleKey: string;
  descriptionKey: string;
  whenToUseKey: string;
  operators: OperatorGuide[];
}

@Component({
  selector: 'app-filter-usage-guide-modal',
  templateUrl: './filter-usage-guide-modal.component.html',
  styleUrls: ['./filter-usage-guide-modal.component.scss'],
})
export class FilterUsageGuideModalComponent {
  // Only the TEXT tab is active. Other tabs (number, date, boolean, options) are commented out
  // until those filter types are confirmed ready for co-counsel use.
  filterTabs: FilterTabGuide[] = [
    {
      id: 'text',
      icon: 'text_fields',
      titleKey: 'FilterGuideTextTitle',
      descriptionKey: 'FilterGuideTextDescription',
      whenToUseKey: 'FilterGuideTextWhenToUse',
      operators: [
        { symbol: '=',   nameKey: 'OpIs',             descriptionKey: 'OpTextIsDesc',           exampleKey: 'OpTextIsExample' },
        { symbol: '≠',   nameKey: 'OpIsNot',          descriptionKey: 'OpTextIsNotDesc',        exampleKey: 'OpTextIsNotExample' },
        { symbol: '∋',   nameKey: 'OpContains',       descriptionKey: 'OpTextContainsDesc',     exampleKey: 'OpTextContainsExample' },
        { symbol: '∌',   nameKey: 'OpDoesNotContain', descriptionKey: 'OpTextNotContainsDesc',  exampleKey: 'OpTextNotContainsExample' },
        { symbol: 'A…',  nameKey: 'OpStartsWith',     descriptionKey: 'OpTextStartsWithDesc',   exampleKey: 'OpTextStartsWithExample' },
        { symbol: '…Z',  nameKey: 'OpEndsWith',       descriptionKey: 'OpTextEndsWithDesc',     exampleKey: 'OpTextEndsWithExample' },
        { symbol: '∅',   nameKey: 'OpIsEmpty',        descriptionKey: 'OpTextIsEmptyDesc',      exampleKey: 'OpTextIsEmptyExample' },
        { symbol: '!∅',  nameKey: 'OpIsNotEmpty',     descriptionKey: 'OpTextIsNotEmptyDesc',   exampleKey: 'OpTextIsNotEmptyExample' },
      ],
    },
    // --- NUMBER tab: commented out — not exposed to co-counsel yet ---
    // {
    //   id: 'number',
    //   icon: 'pin',
    //   titleKey: 'FilterGuideNumberTitle',
    //   descriptionKey: 'FilterGuideNumberDescription',
    //   whenToUseKey: 'FilterGuideNumberWhenToUse',
    //   operators: [
    //     { symbol: '=',  nameKey: 'OpEquals',        descriptionKey: 'OpNumEqDesc',           exampleKey: 'OpNumEqExample' },
    //     { symbol: '≠',  nameKey: 'OpNotEquals',     descriptionKey: 'OpNumNeqDesc',          exampleKey: 'OpNumNeqExample' },
    //     { symbol: '>',  nameKey: 'OpGreaterThan',   descriptionKey: 'OpNumGtDesc',           exampleKey: 'OpNumGtExample' },
    //     { symbol: '<',  nameKey: 'OpLessThan',      descriptionKey: 'OpNumLtDesc',           exampleKey: 'OpNumLtExample' },
    //     { symbol: '≥',  nameKey: 'OpGreaterOrEqual', descriptionKey: 'OpNumGteDesc',         exampleKey: 'OpNumGteExample' },
    //     { symbol: '≤',  nameKey: 'OpLessOrEqual',   descriptionKey: 'OpNumLteDesc',          exampleKey: 'OpNumLteExample' },
    //     { symbol: '∅',  nameKey: 'OpIsEmpty',       descriptionKey: 'OpNumIsEmptyDesc',      exampleKey: 'OpNumIsEmptyExample' },
    //     { symbol: '!∅', nameKey: 'OpIsNotEmpty',    descriptionKey: 'OpNumIsNotEmptyDesc',   exampleKey: 'OpNumIsNotEmptyExample' },
    //   ],
    // },
    // --- DATE tab: commented out — not exposed to co-counsel yet ---
    // {
    //   id: 'date',
    //   icon: 'event',
    //   titleKey: 'FilterGuideDateTitle',
    //   descriptionKey: 'FilterGuideDateDescription',
    //   whenToUseKey: 'FilterGuideDateWhenToUse',
    //   operators: [
    //     { symbol: '>', nameKey: 'OpAfter',   descriptionKey: 'OpDateAfterDesc',   exampleKey: 'OpDateAfterExample' },
    //     { symbol: '<', nameKey: 'OpBefore',  descriptionKey: 'OpDateBeforeDesc',  exampleKey: 'OpDateBeforeExample' },
    //     { symbol: '↔', nameKey: 'OpBetween', descriptionKey: 'OpDateBetweenDesc', exampleKey: 'OpDateBetweenExample' },
    //   ],
    // },
    // --- BOOLEAN tab: commented out — not exposed to co-counsel yet ---
    // {
    //   id: 'boolean',
    //   icon: 'toggle_on',
    //   titleKey: 'FilterGuideBooleanTitle',
    //   descriptionKey: 'FilterGuideBooleanDescription',
    //   whenToUseKey: 'FilterGuideBooleanWhenToUse',
    //   operators: [
    //     { symbol: '✓', nameKey: 'OpTrue',  descriptionKey: 'OpBoolTrueDesc',  exampleKey: 'OpBoolTrueExample' },
    //     { symbol: '✗', nameKey: 'OpFalse', descriptionKey: 'OpBoolFalseDesc', exampleKey: 'OpBoolFalseExample' },
    //     { symbol: '−', nameKey: 'OpAny',   descriptionKey: 'OpBoolAnyDesc',   exampleKey: 'OpBoolAnyExample' },
    //   ],
    // },
    // --- OPTIONS tab: commented out — not exposed to co-counsel yet ---
    // {
    //   id: 'options',
    //   icon: 'list',
    //   titleKey: 'FilterGuideOptionsTitle',
    //   descriptionKey: 'FilterGuideOptionsDescription',
    //   whenToUseKey: 'FilterGuideOptionsWhenToUse',
    //   operators: [
    //     { symbol: '=', nameKey: 'OpIs',     descriptionKey: 'OpOptIsDesc',  exampleKey: 'OpOptIsExample' },
    //     { symbol: '∈', nameKey: 'OpInList', descriptionKey: 'OpOptInDesc',  exampleKey: 'OpOptInExample' },
    //   ],
    // },
  ];

  selectedTabIndex = 0;

  constructor(
    private dialogRef: MatDialogRef<FilterUsageGuideModalComponent>,
    private translate: TranslateService
  ) {}

  onClose(): void {
    this.dialogRef.close();
  }

  downloadPdf(): void {
    const pdfCharMap: Record<string, string> = {
      '→': '->',
      '↔': '<->',
      '≥': '>=',
      '≤': '<=',
      '≠': '!=',
      '∋': 'has',
      '∌': '!has',
      '∅': '(empty)',
      '!∅': '(not empty)',
      '∈': 'in',
      '✓': 'Y',
      '✗': 'N',
      '−': '-',
      '…': '...',
    };
    const pdfSafe = (text: string): string =>
      Object.entries(pdfCharMap).reduce(
        (acc, [from, to]) => acc.split(from).join(to),
        text
      );
    const t = (key: string) => pdfSafe(this.translate.instant(key));

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;
    const marginBottom = 18;
    let cursorY = 18;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(26, 30, 31);
    doc.text(t('FilterUsageGuideTitle'), marginX, cursorY);
    cursorY += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(84, 84, 84);
    const subtitleLines = doc.splitTextToSize(
      t('FilterUsageGuideSubtitle'),
      pageWidth - marginX * 2
    );
    doc.text(subtitleLines, marginX, cursorY);
    cursorY += subtitleLines.length * 4.5 + 6;

    // How-to section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(26, 30, 31);
    doc.text(t('FilterGuideHowToTitle'), marginX, cursorY);
    cursorY += 6;

    const steps = ['FilterGuideStep1', 'FilterGuideStep2', 'FilterGuideStep3', 'FilterGuideStep4', 'FilterGuideStep5'];
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    steps.forEach((key) => {
      const stepLines = doc.splitTextToSize(t(key), pageWidth - marginX * 2 - 4);
      if (cursorY + stepLines.length * 4 > pageHeight - marginBottom) {
        doc.addPage();
        cursorY = 18;
      }
      doc.text(stepLines, marginX + 2, cursorY);
      cursorY += stepLines.length * 4 + 1.5;
    });
    cursorY += 4;

    // Buttons section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(26, 30, 31);
    doc.text(t('FilterGuideButtonsTitle'), marginX, cursorY);
    cursorY += 5;

    const buttons: Array<{ labelKey: string; descKey: string }> = [
      { labelKey: 'FilterGuideButtonApply',   descKey: 'FilterGuideButtonApplyDesc' },
      { labelKey: 'FilterGuideButtonSortAsc',  descKey: 'FilterGuideButtonSortAscDesc' },
      { labelKey: 'FilterGuideButtonSortDesc', descKey: 'FilterGuideButtonSortDescDesc' },
      { labelKey: 'FilterGuideButtonRemove',   descKey: 'FilterGuideButtonRemoveDesc' },
    ];
    buttons.forEach(({ labelKey, descKey }) => {
      const label = t(labelKey);
      const desc = t(descKey);
      const descLines = doc.splitTextToSize(desc, pageWidth - marginX * 2 - 36);
      if (cursorY + Math.max(descLines.length * 4, 6) + 2 > pageHeight - marginBottom) {
        doc.addPage();
        cursorY = 18;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(26, 30, 31);
      doc.text(`[${label}]`, marginX + 2, cursorY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(descLines, marginX + 36, cursorY);
      cursorY += Math.max(descLines.length * 4, 5) + 2;
    });
    cursorY += 4;

    this.filterTabs.forEach((tab) => {
      const tabTitle = t(tab.titleKey);
      const tabDescription = t(tab.descriptionKey);
      const whenToUseLabel = t('FilterGuideWhenToUseLabel');
      const whenToUseText = t(tab.whenToUseKey);

      const descLines = doc.splitTextToSize(
        tabDescription,
        pageWidth - marginX * 2
      );
      const whenLines = doc.splitTextToSize(
        `${whenToUseLabel}: ${whenToUseText}`,
        pageWidth - marginX * 2 - 4
      );
      const headerBlockHeight =
        7 + descLines.length * 4.2 + 3 + whenLines.length * 4 + 5;

      if (cursorY + headerBlockHeight + 30 > pageHeight - marginBottom) {
        doc.addPage();
        cursorY = 18;
      }

      doc.setDrawColor(26, 30, 31);
      doc.setLineWidth(0.8);
      doc.line(marginX, cursorY - 4, marginX, cursorY + headerBlockHeight - 6);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(26, 30, 31);
      doc.text(tabTitle, marginX + 4, cursorY);
      cursorY += 5.5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text(descLines, marginX + 4, cursorY);
      cursorY += descLines.length * 4.2 + 2;

      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(whenLines, marginX + 4, cursorY);
      cursorY += whenLines.length * 4 + 4;

      autoTable(doc, {
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        head: [
          [
            '',
            t('FilterGuideOperatorColumn'),
            t('FilterGuideDescriptionColumn'),
            t('FilterGuideExampleColumn'),
          ],
        ],
        body: tab.operators.map((op) => [
          pdfSafe(op.symbol),
          t(op.nameKey),
          t(op.descriptionKey),
          t(op.exampleKey),
        ]),
        styles: {
          font: 'helvetica',
          fontSize: 9,
          cellPadding: 2.5,
          textColor: [40, 40, 40],
          lineColor: [228, 228, 228],
          lineWidth: 0.1,
          valign: 'top',
        },
        headStyles: {
          fillColor: [26, 30, 31],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
        },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center', fontStyle: 'bold', fontSize: 11 },
          1: { cellWidth: 30, fontStyle: 'bold' },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 'auto', textColor: [90, 90, 90] },
        },
        didDrawPage: (data) => {
          cursorY = data.cursor?.y ?? cursorY;
        },
      });

      cursorY = (doc as any).lastAutoTable.finalY + 8;
    });

    if (cursorY > pageHeight - marginBottom) {
      doc.addPage();
      cursorY = pageHeight - marginBottom - 4;
    }
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(marginX, pageHeight - marginBottom + 4, pageWidth - marginX, pageHeight - marginBottom + 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      t('FilterUsageGuideFooter'),
      pageWidth / 2,
      pageHeight - marginBottom + 9,
      { align: 'center' }
    );

    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 140);
      doc.text(
        `${i} / ${pageCount}`,
        pageWidth - marginX,
        pageHeight - marginBottom + 9,
        { align: 'right' }
      );
    }

    doc.save('filter-usage-guide.pdf');
  }
}
